/**
 * Convierte la lista plana de partidos de bracket de la API al árbol que espera react-tournament-bracket
 * (game.sides.home / .visitor con team, score, seed.sourceGame).
 */

function pick(obj, camel, pascal) {
  if (!obj) return undefined;
  if (obj[camel] !== undefined) return obj[camel];
  if (obj[pascal] !== undefined) return obj[pascal];
  return undefined;
}

/** Alineado con BracketWinnerProgression: Order null al final (como int.MaxValue en C#), luego Name, luego Id. */
function compareBracketFeeders(a, b) {
  const orderKey = (o) => {
    if (o == null || o === '') return 2147483647;
    const n = Number(o);
    return Number.isFinite(n) ? n : 2147483647;
  };
  const d = orderKey(a.order) - orderKey(b.order);
  if (d !== 0) return d;
  const nameCmp = String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  if (nameCmp !== 0) return nameCmp;
  return String(a.id).localeCompare(String(b.id));
}

function normalizeMatch(raw) {
  if (!raw || !raw.id) return null;
  return {
    id: raw.id,
    tournamentId: pick(raw, 'tournamentId', 'TournamentId'),
    nextMatchId: pick(raw, 'nextMatchId', 'NextMatchId') ?? null,
    name: pick(raw, 'name', 'Name') ?? '',
    fieldName: String(pick(raw, 'fieldName', 'FieldName') ?? '').trim(),
    dayHour: pick(raw, 'dayHour', 'DayHour'),
    localGoals: pick(raw, 'localGoals', 'LocalGoals'),
    foreignGoals: pick(raw, 'foreignGoals', 'ForeignGoals'),
    localTeam: pick(raw, 'localTeam', 'LocalTeam'),
    foreignTeam: pick(raw, 'foreignTeam', 'ForeignTeam'),
    groupPositionLocal: pick(raw, 'groupPositionLocal', 'GroupPositionLocal'),
    groupPositionForeign: pick(raw, 'groupPositionForeign', 'GroupPositionForeign'),
    crossGroupRankPositionLocal: pick(raw, 'crossGroupRankPositionLocal', 'CrossGroupRankPositionLocal') ?? null,
    crossGroupRankIndexLocal: pick(raw, 'crossGroupRankIndexLocal', 'CrossGroupRankIndexLocal') ?? null,
    crossGroupRankPositionForeign: pick(raw, 'crossGroupRankPositionForeign', 'CrossGroupRankPositionForeign') ?? null,
    crossGroupRankIndexForeign: pick(raw, 'crossGroupRankIndexForeign', 'CrossGroupRankIndexForeign') ?? null,
    completed: pick(raw, 'completed', 'Completed') ?? false,
    isActive: pick(raw, 'isActive', 'IsActive') ?? false,
    order: pick(raw, 'order', 'Order')
  };
}

const ORDINAL_NAMES_BRACKET = ['', '1º', '2º', '3º', '4º', '5º', '6º', '7º', '8º'];

function ordinalBracket(n) {
  const i = Number(n);
  if (!Number.isFinite(i) || i < 1) return `${n}º`;
  return ORDINAL_NAMES_BRACKET[i] || `${i}º`;
}

function formatCrossGroupRankLabel(position, index) {
  const p = Number(position);
  const i = Number(index);
  if (!Number.isFinite(p) || !Number.isFinite(i) || p < 1 || i < 1) return '';
  if (i === 1) return `Mejor ${ordinalBracket(p)} entre grupos`;
  return `${ordinalBracket(i)} mejor ${ordinalBracket(p)} entre grupos`;
}

function teamDtoToTeam(t) {
  if (!t || !t.id) return null;
  const name = t.name ?? t.Name ?? '';
  const shield = t.shield ?? t.Shield ?? null;
  return { id: t.id, name, shield };
}

/** Misma semántica que formatGroupPositionLabel en setup (API anida group + name de la plaza). */
function isLikelyThirdPlaceMatchName(name) {
  const n = String(name || '').toLowerCase();
  return (
    n.includes('puesto') ||
    n.includes('3º') ||
    n.includes('3°') ||
    n.includes('tercero') ||
    n.includes('cuarto') ||
    n.includes('3rd') ||
    n.includes('third') ||
    n.includes('fourth')
  );
}

function formatGroupPositionLabel(p) {
  if (!p) return '';
  const g = p.group?.name ?? p.Group?.name ?? 'Grupo';
  const slot =
    String(p.name ?? p.Name ?? '')
      .trim() || `Puesto ${p.position ?? p.Position ?? ''}`;
  return `${g} — ${slot}`;
}

function teamFromGroupPositionNested(gp) {
  if (!gp) return null;
  const t = gp.team ?? gp.Team;
  return teamDtoToTeam(t);
}

/**
 * Texto visible en el bracket: equipo resuelto, o equipo colgado de la plaza, o etiqueta de plaza (ej. "Grupo A — 1º clasificado").
 */
function bracketSideDisplayTeam(teamDto, groupPosition, matchId, sideKey, crossGroupRankPosition, crossGroupRankIndex) {
  const tm = teamDtoToTeam(teamDto);
  if (tm?.name) return tm;

  const fromPos = teamFromGroupPositionNested(groupPosition);
  if (fromPos?.name) return fromPos;

  const slotLabel = formatGroupPositionLabel(groupPosition);
  if (slotLabel) {
    const posId = groupPosition.id ?? groupPosition.Id;
    return {
      id: posId ?? `bracket-${matchId}-${sideKey}`,
      name: slotLabel,
      shield: null
    };
  }

  const crossLabel = formatCrossGroupRankLabel(crossGroupRankPosition, crossGroupRankIndex);
  if (crossLabel) {
    return {
      id: `bracket-${matchId}-${sideKey}-cross`,
      name: crossLabel,
      shield: null
    };
  }

  return null;
}

/**
 * ¿La plaza está FIJADA por una plaza de grupo o por el pool «mejor entre grupos»?
 * Solo esos sembrados deben impedir que un feeder ocupe la plaza (el feeder se
 * redirige a la otra). A diferencia de {@link bracketSideDisplayTeam}, un equipo
 * ya resuelto NO cuenta como sembrado: cuando un partido alimentador termina, su
 * ganador se copia a esta plaza, y aun así queremos seguir dibujando ese partido
 * alimentador debajo (si no, el partido finalizado desaparecería del cuadro).
 */
function bracketSideIsSeeded(groupPosition, crossGroupRankPosition, crossGroupRankIndex) {
  if (teamFromGroupPositionNested(groupPosition)?.name) return true;
  if (formatGroupPositionLabel(groupPosition)) return true;
  if (formatCrossGroupRankLabel(crossGroupRankPosition, crossGroupRankIndex)) return true;
  return false;
}

/**
 * @param {Array<object>} flatMatches respuesta de GET .../bracketmatchs/component2
 * @returns {Array<object>} raíces (partidos sin NextMatchId o con next huérfano), cada una lista para <Bracket game={...} />
 */
export function transformFlatBracketMatchesToRoots(flatMatches) {
  if (!Array.isArray(flatMatches) || flatMatches.length === 0) return [];

  const matches = flatMatches.map(normalizeMatch).filter(Boolean);
  const byId = new Map(matches.map((m) => [m.id, m]));
  const childrenByParent = new Map();

  for (const m of matches) {
    if (m.nextMatchId && byId.has(m.nextMatchId)) {
      const list = childrenByParent.get(m.nextMatchId);
      if (list) list.push(m);
      else childrenByParent.set(m.nextMatchId, [m]);
    }
  }

  for (const [, arr] of childrenByParent) {
    arr.sort(compareBracketFeeders);
  }

  const roots = matches.filter(
    (m) => m.nextMatchId == null || !byId.has(m.nextMatchId)
  );

  const cache = new Map();

  function buildGame(match, isBracketRoot = false) {
    if (cache.has(match.id)) return cache.get(match.id);

    const children = childrenByParent.get(match.id) || [];

    // ¿La plaza está sembrada por una plaza de grupo o pool «mejor entre grupos»?
    // Solo ese sembrado impide que un feeder ocupe la plaza (se redirige a la otra).
    // OJO: un equipo ya resuelto NO bloquea el feeder; cuando un partido alimentador
    // termina su ganador se copia aquí, pero el partido debe seguir dibujándose debajo.
    const homeSlotSeeded = bracketSideIsSeeded(
      match.groupPositionLocal,
      match.crossGroupRankPositionLocal,
      match.crossGroupRankIndexLocal
    );
    const visitorSlotSeeded = bracketSideIsSeeded(
      match.groupPositionForeign,
      match.crossGroupRankPositionForeign,
      match.crossGroupRankIndexForeign
    );

    // Cada feeder ocupa la plaza que marca su `order` (1 = local, 2 = visitante),
    // igual que BracketWinnerProgression en el backend. No se reparte por la posición
    // relativa entre feeders: un destino puede tener una plaza fijada por grupo y el
    // feeder restante apunta a la otra (p. ej. Octavos con 1º de grupo + repesca order 2).
    // Los feeders con order distinto de 1/2 (freeform) rellenan los huecos libres en el
    // orden estable de compareBracketFeeders.
    let homeChild = null;
    let visitorChild = null;
    const leftoverChildren = [];
    for (const child of children) {
      const childOrder = Number(child.order);
      if (childOrder === 1 && homeChild == null && !homeSlotSeeded) homeChild = child;
      else if (childOrder === 2 && visitorChild == null && !visitorSlotSeeded) visitorChild = child;
      else leftoverChildren.push(child);
    }
    for (const child of leftoverChildren) {
      if (homeChild == null && !homeSlotSeeded) homeChild = child;
      else if (visitorChild == null && !visitorSlotSeeded) visitorChild = child;
    }

    const sideFrom = (teamDto, goals, childMatch, groupPosition, sideKey, crossPos, crossIdx) => {
      let displayTeam = bracketSideDisplayTeam(teamDto, groupPosition, match.id, sideKey, crossPos, crossIdx);

      // Sin equipo ni plaza, pero alimentado por otro partido: mostrar "Ganador <fase>".
      if (!displayTeam && childMatch != null) {
        const childName = String(childMatch.name || '').trim();
        displayTeam = {
          id: `bracket-${match.id}-${sideKey}-winner`,
          name: childName ? `Ganador ${childName}` : 'Por definir',
          shield: null,
          placeholder: true
        };
      }

      const seed =
        childMatch != null
          ? {
              rank: 1,
              displayName: displayTeam?.name || '',
              sourceGame: buildGame(childMatch, false)
            }
          : null;
      return {
        team: displayTeam,
        score: { score: goals != null && goals !== '' ? Number(goals) : 0 },
        seed
      };
    };

    const championshipRoot = isBracketRoot && !isLikelyThirdPlaceMatchName(match.name);

    const game = {
      id: match.id,
      name: match.name,
      fieldName: match.fieldName || '',
      scheduled: match.dayHour,
      completed: match.completed,
      isActive: match.isActive,
      /** Solo el partido final del cuadro principal (no 3º/4º ni otros árboles sueltos). */
      isBracketRoot: championshipRoot,
      sides: {
        home: sideFrom(
          match.localTeam,
          match.localGoals,
          homeChild,
          match.groupPositionLocal,
          'local',
          match.crossGroupRankPositionLocal,
          match.crossGroupRankIndexLocal
        ),
        visitor: sideFrom(
          match.foreignTeam,
          match.foreignGoals,
          visitorChild,
          match.groupPositionForeign,
          'foreign',
          match.crossGroupRankPositionForeign,
          match.crossGroupRankIndexForeign
        )
      }
    };

    cache.set(match.id, game);
    return game;
  }

  return roots.map((f) => buildGame(f, true));
}
